/** Resize + compress an image for email signatures (small inline-friendly payload). */

const DEFAULT_MAX_WIDTH = 200;
const DEFAULT_MAX_BYTES = 120 * 1024; // 120 KB per image

function loadImageFromFile(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Could not load image"));
    };
    img.src = url;
  });
}

function canvasToBlob(canvas, type, quality) {
  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), type, quality);
  });
}

/**
 * @returns {Promise<{ dataUrl: string, width: number, height: number, bytes: number }>}
 */
export async function resizeImageForEmail(file, {
  maxWidth = DEFAULT_MAX_WIDTH,
  maxBytes = DEFAULT_MAX_BYTES,
} = {}) {
  if (!file || !String(file.type || "").startsWith("image/")) {
    throw new Error("Please choose an image file (PNG, JPG, GIF, WebP)");
  }
  if (file.size > 5 * 1024 * 1024) {
    throw new Error("Image must be under 5 MB before resize");
  }

  const img = await loadImageFromFile(file);
  const scale = img.width > maxWidth ? maxWidth / img.width : 1;
  const w = Math.max(1, Math.round(img.width * scale));
  const h = Math.max(1, Math.round(img.height * scale));

  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(img, 0, 0, w, h);

  const preferPng = file.type === "image/png" || file.type === "image/gif";
  let mime = preferPng ? "image/png" : "image/jpeg";
  let quality = preferPng ? undefined : 0.88;
  let blob = await canvasToBlob(canvas, mime, quality);

  // Compress JPEG quality until under cap (PNG: accept as-is if small enough).
  if (mime === "image/jpeg") {
    while (blob && blob.size > maxBytes && quality > 0.45) {
      quality -= 0.08;
      blob = await canvasToBlob(canvas, mime, quality);
    }
  }

  if (!blob) throw new Error("Failed to encode image");
  if (blob.size > maxBytes && mime === "image/png") {
    mime = "image/jpeg";
    quality = 0.85;
    blob = await canvasToBlob(canvas, mime, quality);
    while (blob && blob.size > maxBytes && quality > 0.45) {
      quality -= 0.08;
      blob = await canvasToBlob(canvas, mime, quality);
    }
  }

  if (!blob || blob.size > maxBytes) {
    throw new Error(`Image still too large after resize (max ${Math.round(maxBytes / 1024)} KB). Use a smaller logo.`);
  }

  const dataUrl = await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(reader.error || new Error("Read failed"));
    reader.readAsDataURL(blob);
  });

  return { dataUrl, width: w, height: h, bytes: blob.size };
}

export function signatureImgTag({ dataUrl, width, height }) {
  const w = width || 200;
  const h = height || "auto";
  return `<img src="${dataUrl}" alt="" width="${w}" height="${typeof h === "number" ? h : "auto"}" style="max-width:${w}px;height:auto;display:block;border:0;outline:none;text-decoration:none;" />`;
}
